import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import LoadingWrapper from 'components/common/LoadingWrapper';
import SearchInput from 'components/common/SearchInput';
import GraphReference from 'components/Forecasting/GraphReference';
import ProjectList from 'components/Forecasting/ProjectsList';
import RevenueGraph from 'components/Forecasting/RevenueGraph';
import { FORECASTING_GRAPH_REFERENCES, PROJECT_STATUS_TYPES } from 'constants/constants';
import { useRequest } from 'hooks';
import { getGraph, getProjects } from 'state/actions/forecastingActions';
import {
  aggregateUnderContractFromProjects,
  aggregatePipelineFromProjects
} from 'utils/forecastingUtilities';
import { filterList } from 'utils/reducersUtilities';

import {
  PageWrapper,
  Container,
  HeaderSection,
  TitleRow,
  Title,
  ProjectCountsRow,
  CountBadge,
  Separator,
  KpiGrid,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  ChartCard,
  ChartTitle,
  SearchRow
} from './styles';

const countMonths = 3;
const chartMonths = 12;

const Forecasting = () => {
  const intl = useIntl();
  const [search, setSearch] = useState();
  const [projects, setProjects] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [includedPipelineIds, setIncludedPipelineIds] = useState(new Set());
  const [winProbabilities, setWinProbabilities] = useState({});

  const [{ isPending: isProjectLoading }] = useRequest(getProjects, {
    fulfilledCallback: payload => {
      const list = Array.isArray(payload) ? payload : payload.projects || [];
      setProjects(list);
      const pipelineIds = list
        .filter(p => p.status === PROJECT_STATUS_TYPES.PLANNED)
        .map(p => p.id);
      setIncludedPipelineIds(new Set(pipelineIds));
    },
    params: { forecastedMonthsAhead: chartMonths }
  });

  const [{ isPending: isGraphLoading }] = useRequest(getGraph, {
    fulfilledCallback: payload => {
      setGraphData(payload);
    }
  });

  const pipelineProjects = useMemo(
    () => (projects || []).filter(p => p.status === PROJECT_STATUS_TYPES.PLANNED),
    [projects]
  );

  const currentProjects = useMemo(
    () => (projects || []).filter(p => p.status !== PROJECT_STATUS_TYPES.PLANNED),
    [projects]
  );

  const selectedPipelineProjects = useMemo(
    () => pipelineProjects.filter(p => includedPipelineIds.has(p.id)),
    [pipelineProjects, includedPipelineIds]
  );

  const filteredPipelineProjects = useMemo(
    () => filterList(pipelineProjects, search),
    [pipelineProjects, search]
  );

  const filteredCurrentProjects = useMemo(
    () => filterList(currentProjects, search),
    [currentProjects, search]
  );

  const revenueGraphData = useMemo(() => {
    const withUnderContract = aggregateUnderContractFromProjects(
      graphData.yearMonths,
      currentProjects
    );
    return aggregatePipelineFromProjects(
      withUnderContract,
      selectedPipelineProjects,
      winProbabilities
    );
  }, [graphData.yearMonths, currentProjects, selectedPipelineProjects, winProbabilities]);

  const underContractTotal = useMemo(
    () => revenueGraphData?.reduce((sum, m) => sum + (m.underContract || 0), 0) || 0,
    [revenueGraphData]
  );

  const pipelineTotal = useMemo(
    () => revenueGraphData?.reduce((sum, m) => sum + (m.pipeline || 0), 0) || 0,
    [revenueGraphData]
  );

  const totalForecast = underContractTotal + pipelineTotal;

  const handleProbabilityChange = (projectId, value) => {
    setWinProbabilities(prev => ({ ...prev, [projectId]: value }));
  };

  const handleTogglePipeline = projectId => {
    setIncludedPipelineIds(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  return (
    <PageWrapper>
      <Container>
        <HeaderSection>
          <TitleRow>
            <Title>{intl.formatMessage({ id: 'forecasting.mainPage.title' })}</Title>
            <ProjectCountsRow>
              <span>{intl.formatMessage({ id: 'forecasting.mainPage.activeProjects' })}</span>
              <CountBadge $variant="active">{currentProjects.length}</CountBadge>
              <Separator>|</Separator>
              <span>{intl.formatMessage({ id: 'forecasting.mainPage.pipelineDeals' })}</span>
              <CountBadge $variant="pipeline">{pipelineProjects.length}</CountBadge>
              <Separator>|</Separator>
              <span>{intl.formatMessage({ id: 'forecasting.mainPage.totalPortfolio' })}</span>
              <CountBadge>{projects.length}</CountBadge>
            </ProjectCountsRow>
          </TitleRow>

          <KpiGrid>
            <GraphReference
              type={FORECASTING_GRAPH_REFERENCES.UNDER_CONTRACT}
              total={underContractTotal}
            />
            <GraphReference
              type={FORECASTING_GRAPH_REFERENCES.PIPELINE_SELECTED}
              total={pipelineTotal}
            />
            <GraphReference
              type={FORECASTING_GRAPH_REFERENCES.TOTAL_FORECAST}
              total={totalForecast}
            />
          </KpiGrid>
        </HeaderSection>

        <LoadingWrapper isLoading={isGraphLoading || isProjectLoading}>
          <ChartCard>
            <ChartTitle>{intl.formatMessage({ id: 'forecasting.mainPage.chartTitle' })}</ChartTitle>
            <RevenueGraph data={revenueGraphData} />
          </ChartCard>
        </LoadingWrapper>

        <SearchRow>
          <SearchInput
            placeholderId="filter.financial.projects"
            value={search}
            onChange={setSearch}
            variant="new"
          />
        </SearchRow>

        <Card>
          <CardHeader>
            <CardTitle>
              {`${intl.formatMessage({ id: 'forecasting.mainPage.pipeline' })} (${
                pipelineProjects.length
              })`}
            </CardTitle>
            <CardDescription>
              {intl.formatMessage({ id: 'forecasting.mainPage.pipelineDescription' })}
            </CardDescription>
          </CardHeader>
          <ProjectList
            forecastingProjects={filteredPipelineProjects}
            isLoading={isProjectLoading}
            countMonths={countMonths}
            showToggle
            includedIds={includedPipelineIds}
            onToggle={handleTogglePipeline}
            onSetIncludedIds={setIncludedPipelineIds}
            winProbabilities={winProbabilities}
            onProbabilityChange={handleProbabilityChange}
          />
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {`${intl.formatMessage({ id: 'forecasting.mainPage.currentProjects' })} (${
                currentProjects.length
              })`}
            </CardTitle>
            <CardDescription>
              {intl.formatMessage({ id: 'forecasting.mainPage.currentProjectsDescription' })}
            </CardDescription>
          </CardHeader>
          <ProjectList
            forecastingProjects={filteredCurrentProjects}
            isLoading={isProjectLoading}
            countMonths={countMonths}
          />
        </Card>
      </Container>
    </PageWrapper>
  );
};

export default Forecasting;
